import React, { useMemo } from "react";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

import ChartCard from "./ChartCard";
import ChartTooltip from "./ChartTooltip";
import {
  useChartTheme,
  formatCurrency,
  formatCompactCurrency,
  formatNumber,
} from "./dashboardUtils";

const ROW_HEIGHT = 36;
const MAX_CHART_ROWS = 10;

const truncate = (text, max = 26) =>
  text && text.length > max ? `${text.slice(0, max - 1)}…` : text;

// Ranks entities (hotels, condos, packages) by booked revenue. One series, so
// every bar is the same colour; the bar length carries the comparison.
const BreakdownCard = ({ title, subtitle, rows, nameLabel, extraColumn }) => {
  const chartTheme = useChartTheme();
  const totalBooked = useMemo(
    () => (rows || []).reduce((acc, r) => acc + r.booked, 0),
    [rows]
  );
  const chartRows = (rows || []).slice(0, MAX_CHART_ROWS);
  const hiddenCount = (rows || []).length - chartRows.length;

  const chart = (
    <>
      <ResponsiveContainer
        width="100%"
        height={Math.max(chartRows.length * ROW_HEIGHT + 40, 120)}
      >
        <BarChart
          data={chartRows}
          layout="vertical"
          margin={{ top: 0, right: 16, left: 0, bottom: 0 }}
          barCategoryGap={6}
        >
          <CartesianGrid horizontal={false} stroke={chartTheme.grid} />
          <XAxis
            type="number"
            stroke={chartTheme.grid}
            tick={{ fill: chartTheme.muted, fontSize: 12 }}
            tickLine={false}
            tickFormatter={formatCompactCurrency}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={170}
            stroke={chartTheme.grid}
            tick={{ fill: chartTheme.text, fontSize: 12 }}
            tickLine={false}
            tickFormatter={(name) => truncate(name)}
          />
          <Tooltip
            cursor={{ fill: chartTheme.grid }}
            content={
              <ChartTooltip
                labelFormatter={(label, payload) => {
                  const row = payload[0] && payload[0].payload;
                  return row
                    ? `${row.name} · ${formatNumber(row.orders)} orders`
                    : label;
                }}
                formatter={formatCurrency}
              />
            }
          />
          <Bar
            dataKey="booked"
            name="Booked"
            fill={chartTheme.series[0]}
            radius={[0, 4, 4, 0]}
            maxBarSize={24}
          />
        </BarChart>
      </ResponsiveContainer>
      {hiddenCount > 0 && (
        <p style={{ color: chartTheme.muted, fontSize: 12, margin: 0 }}>
          {`Top ${MAX_CHART_ROWS} shown. ${hiddenCount} more in the table view.`}
        </p>
      )}
    </>
  );

  const table = (
    <TableContainer style={{ maxHeight: 420 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>{nameLabel}</TableCell>
            {extraColumn && <TableCell>{extraColumn.label}</TableCell>}
            <TableCell align="right">Orders</TableCell>
            <TableCell align="right">Nights</TableCell>
            <TableCell align="right">Avg nights</TableCell>
            <TableCell align="right">Booked</TableCell>
            <TableCell align="right">Share</TableCell>
            <TableCell align="right">Collected</TableCell>
            <TableCell align="right">Avg order</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(rows || []).map((row) => (
            <TableRow key={`${row.property_type || ""}${row.name}`} hover>
              <TableCell>{row.name}</TableCell>
              {extraColumn && <TableCell>{extraColumn.render(row)}</TableCell>}
              <TableCell align="right">{formatNumber(row.orders)}</TableCell>
              <TableCell align="right">{formatNumber(row.nights)}</TableCell>
              <TableCell align="right">{row.avg_nights}</TableCell>
              <TableCell align="right">{formatCurrency(row.booked)}</TableCell>
              <TableCell align="right">
                {totalBooked
                  ? `${((row.booked / totalBooked) * 100).toFixed(1)}%`
                  : "—"}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(row.collected)}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(row.avg_order_value)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      isEmpty={!rows || rows.length === 0}
      chart={chart}
      table={table}
    />
  );
};

export default BreakdownCard;
